
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import AmountDisplayerFilterable from '@/components/reports-v3/components/filterables/AmountDisplayerFilterable.vue';
import { getTableName } from '@/worker/db/sqlHelpers';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import { PermissionsGroup } from '@/store/models.def';
import IncludeCreditNoteToggle from '@/components/IncludeCreditNoteToggle.vue';
import IncludeDebitNoteToggle from '@/components/IncludeDebitNoteToggle.vue';
import SlPhModuleOnlyToggle from '@/components/SlPhModuleOnlyToggle.vue';
import settingx from '@/store/modules/settingx';

@Component({
	components: {
		AmountDisplayerFilterable,
		IncludeCreditNoteToggle,
		IncludeDebitNoteToggle,
		SlPhModuleOnlyToggle,
	},
})
export default class PurchaseTotalPurchaseNumberDisplayer extends FilterWidget {
	public get permissionIds(): PermissionsGroup[] {
		return ['purchases'];
	}

	public filterIds: Array<
		| 'date'
		| 'dateAsOf'
		| 'dateRange'
		| 'stockItems'
		| 'agents'
		| 'customers'
		| 'suppliers'
	> = ['dateRange'];

	public amount: number = 0;

	public get includeCreditNote() {
		return settingx.now.includeCreditNote;
	}

	public get includeDebitNote() {
		return settingx.now.includeDebitNote;
	}

	public get slPhModuleOnly() {
		return settingx.now.slPhModuleOnly;
	}

	public get expensiveHook() {
		const {
			selectedDateRange,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		} = this;
		return JSON.stringify([
			selectedDateRange,
			includeCreditNote,
			includeDebitNote,
			slPhModuleOnly,
		]);
	}

	public async expensiveCalc() {
		const jobId = this.currentJobId;
		this.generateLoadingText(0.2, 0.3);

		const queryHead = await this._ensureTablesLoaded(['purchases', 'purchaseReturn', 'supplierTrades'] as any[]);
		this.checkInterrupted(jobId);
		this.generateLoadingText(0.3, 0.9);

		const dateFilter = `TRY_CAST("DOCDATE" AS BIGINT) >= ${this.selectedDateRange[0]} AND TRY_CAST("DOCDATE" AS BIGINT) <= ${this.selectedDateRange[1]}`;

		let purchaseAmount = 0;

		if (this.slPhModuleOnly) {
			// PH Mode - use purchases + purchaseReturn
			const docTypes = this.includeDebitNote ? ['PI', 'CP', 'SD'] : ['PI', 'CP'];
			const docTypeList = docTypes.map((d) => `'${d}'`).join(', ');

			const phResult = await queryHead.rawQuery(
				`SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total FROM ${getTableName('purchases')} WHERE ${dateFilter} AND "DOCTYPE" IN (${docTypeList})`,
			);
			this.checkInterrupted(jobId);

			const phAmount = (phResult[0] && Number(phResult[0].total)) || 0;

			let cnAmount = 0;
			if (this.includeCreditNote) {
				const cnResult = await queryHead.rawQuery(
					`SELECT SUM(TRY_CAST("AMOUNT" AS DOUBLE)) as total FROM ${getTableName('purchaseReturn')} WHERE ${dateFilter}`,
				);
				this.checkInterrupted(jobId);
				cnAmount = (cnResult[0] && Number(cnResult[0].total)) || 0;
			}

			purchaseAmount = phAmount - cnAmount;
		} else {
			// AP Mode - use supplierTrades
			const docTypes = this.includeDebitNote ? ['PI', 'SD'] : ['PI'];
			const docTypeList = docTypes.map((d) => `'${d}'`).join(', ');

			const apResult = await queryHead.rawQuery(
				`SELECT SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total FROM ${getTableName('supplierTrades')} WHERE ${dateFilter} AND "DOCTYPE" IN (${docTypeList})`,
			);
			this.checkInterrupted(jobId);

			const apAmount = (apResult[0] && Number(apResult[0].total)) || 0;

			let cnAmount = 0;
			if (this.includeCreditNote) {
				const cnResult = await queryHead.rawQuery(
					`SELECT SUM(TRY_CAST("LOCALAMOUNT" AS DOUBLE)) as total FROM ${getTableName('supplierTrades')} WHERE ${dateFilter} AND "DOCTYPE" = 'SC'`,
				);
				this.checkInterrupted(jobId);
				cnAmount = (cnResult[0] && Number(cnResult[0].total)) || 0;
			}

			purchaseAmount = apAmount - cnAmount;
		}

		this.amount = purchaseAmount;

		this.generateLoadingText(1);
		this.saveHistory('amount');
	}
}
